import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { leaderboardApi, ApiError } from '~/lib/api';
import { useState } from 'react';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { InlineError } from '~/components/ErrorPage';
import { clsx } from 'clsx';

type Period = 'week' | 'month' | 'all';

function LeaderboardContent() {
  const [period, setPeriod] = useState<Period>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['leaderboard', period],
    queryFn: () => leaderboardApi.get(period),
  });

  const traders = data?.traders || [];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-text-primary">Leaderboard</h1>
        <p className="text-sm sm:text-base text-text-secondary">Trader performance rankings</p>
      </div>

      {/* Period tabs */}
      <div className="flex gap-2">
        {([
          ['week', 'Week'],
          ['month', 'Month'],
          ['all', 'All Time'],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            onClick={() => setPeriod(value)}
            className={clsx(
              'px-4 py-2 rounded text-sm font-medium font-mono tracking-wide transition-all duration-150',
              period === value
                ? 'bg-accent text-surface-0'
                : 'bg-surface-3 text-text-secondary hover:text-text-primary'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        </div>
      )}

      {error && (
        <InlineError
          error={error instanceof ApiError ? error : (error as Error)}
        />
      )}

      {!isLoading && !error && traders.length === 0 && (
        <div className="card p-8 text-center">
          <div className="text-4xl mb-4">🏆</div>
          <p className="text-text-secondary">
            No closed trades yet{period !== 'all' ? ` this ${period}` : ''}. Rankings will appear once traders close positions.
          </p>
        </div>
      )}

      {traders.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full min-w-[400px]">
            <thead className="glass-table-header">
              <tr>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-text-muted font-medium w-10">#</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-text-muted font-medium">Trader</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-text-muted font-medium">Trades</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-text-muted font-medium hidden sm:table-cell">Win Rate</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-text-muted font-medium">Total Return</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-text-muted font-medium hidden md:table-cell">Best Trade</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-[11px] font-mono uppercase tracking-widest text-text-muted font-medium hidden sm:table-cell">Total P/L</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {traders.map((trader) => (
                <tr key={trader.trader_id} className="hover:bg-surface-2/50 transition-colors">
                  <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-text-secondary font-mono font-medium">
                    {trader.rank}
                  </td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3">
                    <Link
                      to={`/traders/${trader.trader_id}`}
                      className="text-xs sm:text-sm font-medium text-text-primary hover:text-accent transition-colors"
                    >
                      {trader.display_name}
                    </Link>
                    {trader.display_name !== trader.trader_username && (
                      <div className="text-xs text-text-muted">{trader.trader_username}</div>
                    )}
                  </td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3 text-right text-xs sm:text-sm text-text-secondary font-mono">
                    {trader.total_trades}
                  </td>
                  <td className={clsx(
                    'px-2 sm:px-4 py-2 sm:py-3 text-right text-xs sm:text-sm font-mono hidden sm:table-cell',
                    trader.win_rate >= 50 ? 'text-profit' : 'text-loss'
                  )}>
                    {trader.win_rate}%
                  </td>
                  <td className={clsx(
                    'px-2 sm:px-4 py-2 sm:py-3 text-right text-xs sm:text-sm font-mono font-semibold',
                    trader.total_return_pct > 0
                      ? 'text-profit'
                      : trader.total_return_pct < 0
                        ? 'text-loss'
                        : 'text-text-secondary'
                  )}>
                    {trader.total_return_pct > 0 ? '+' : ''}{trader.total_return_pct.toFixed(2)}%
                  </td>
                  <td className={clsx(
                    'px-2 sm:px-4 py-2 sm:py-3 text-right text-xs sm:text-sm font-mono hidden md:table-cell',
                    trader.best_trade_pct != null && trader.best_trade_pct > 0
                      ? 'text-profit'
                      : trader.best_trade_pct != null && trader.best_trade_pct < 0
                        ? 'text-loss'
                        : 'text-text-secondary'
                  )}>
                    {trader.best_trade_pct != null
                      ? `${trader.best_trade_pct > 0 ? '+' : ''}${trader.best_trade_pct.toFixed(2)}%`
                      : '-'}
                  </td>
                  <td className={clsx(
                    'px-2 sm:px-4 py-2 sm:py-3 text-right text-xs sm:text-sm font-mono hidden sm:table-cell',
                    trader.total_realized_pnl > 0
                      ? 'text-profit'
                      : trader.total_realized_pnl < 0
                        ? 'text-loss'
                        : 'text-text-secondary'
                  )}>
                    ${Math.abs(trader.total_realized_pnl).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function LeaderboardPage() {
  return (
    <ErrorBoundary>
      <LeaderboardContent />
    </ErrorBoundary>
  );
}
